import React, { Fragment, useState } from 'react';

import { WidthObserver } from '@atlaskit/width-detector';

import { getLogSafeErrorAttributes, getReactErrorAttributes } from '@confluence/monitoring';

import { Attribution } from './Attribution';
import type { AttributionContextValue } from './AttributionContext';
import type { ErrorComponentProps } from './GenericErrorBoundary';

// This code was copied from @confluence/splunk/src/builder.ts to resolve a circular dependency.
// If any changes are made here, they should also be made in that file.
const getUrlForQuery = (query: string): string =>
	`https://splunk.paas-inf.net/en-US/app/search/search?q=search%20${encodeURIComponent(
		query,
	)}&display.page.search.mode=smart`;

export function DevErrorDetail({ attribution, error, errorInfo }: ErrorComponentProps) {
	const reactErrorAttributes = getReactErrorAttributes(error);
	const errorHashCode = reactErrorAttributes ? reactErrorAttributes.errorHashCode : undefined;
	const traceId = getLogSafeErrorAttributes(error).traceId;

	let splunkQueryText: string | undefined;
	let splunkQueryHref: string | undefined;
	if (errorHashCode) {
		splunkQueryText = `ext.event.attributes.errorHashCode="${errorHashCode}"`;
		splunkQueryHref = getUrlForQuery(
			[
				'eventtype="micros_analytics-service"',
				'ext.event.product=confluence',
				splunkQueryText,
			].join('\n'),
		);
	}

	const [width, setWidth] = useState(0);

	return (
		<Fragment>
			<WidthObserver setWidth={setWidth} />
			<p>You can also discuss this in the {getDevSupportChannelLink(attribution)} Slack channel.</p>
			{splunkQueryText ? (
				<p>
					You can search{' '}
					<a href={splunkQueryHref}>
						<span>Splunk</span>
					</a>{' '}
					for <b>{splunkQueryText}</b>. g{' '}
				</p>
			) : null}
			{traceId ? (
				<p>
					Trace ID: <b>{traceId}</b>
				</p>
			) : null}
			<p>
				Error:
				{width ? (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<div style={{ overflow: 'scroll', width }}>
						<pre>
							{`${error.message || String(error)}`}
							{errorInfo && `${errorInfo.componentStack}`}
						</pre>
					</div>
				) : null}
			</p>
		</Fragment>
	);
}

function getDevSupportChannelLink(attribution: AttributionContextValue) {
	let href: string;
	let text: string;
	switch (attribution) {
		case Attribution.PAGE_MODES:
			href = 'https://atlassian.slack.com/messages/CKQ8ZJSUQ';
			text = '#cc-page-modes';
			break;
		case Attribution.PAGE_EXTENSIONS:
			href = 'https://atlassian.slack.com/messages/CKCR3SVLK';
			text = '#cc-page-extensions';
			break;
		case Attribution.COMMENTS:
			href = 'https://atlassian.slack.com/messages/C051ARA9QGH';
			text = '#cc-comments';
			break;
		case Attribution.DISCO:
			href = 'https://atlassian.slack.com/messages/CFGSEJDFT';
			text = '#cc-disco';
			break;
		case Attribution.TAILORED_EXPERIENCES:
			href = 'https://atlassian.slack.com/messages/CHA5YUDHQ';
			text = '#cc-tailored-exp';
			break;
		case Attribution.ECOSYSTEM:
			href = 'https://atlassian.slack.com/archives/CFHD7ERB4';
			text = '#cc-integrations-ecosystem-team';
			break;
		case Attribution.WHITEBOARDS:
			href = 'https://atlassian.slack.com/archives/C02PTHXKDUY';
			text = '#help-confluence-whiteboards';
		case Attribution.ADMIN_EXPERIENCE:
			href = 'https://atlassian.slack.com/archives/C0441RTKC3E';
			text = '#cc-admin-exp';
			break;
		case Attribution.PAGE_EXPERIENCES:
			href = 'https://atlassian.enterprise.slack.com/archives/C07CPP220FM';
			text = '#cc-page-experiences';
		default:
			href = 'https://atlassian.slack.com/messages/CFJ90MFKQ';
			text = '#cc-frontend';
			break;
	}
	return (
		<a target="_blank" href={href}>
			{text}
		</a>
	);
}
